import { io } from 'socket.io-client';

// Same-origin in production (Caddy proxies /api and /socket.io); override for local dev
const API_BASE = process.env.REACT_APP_API_URL || '';

let socket = null;
let socketLeaderId = null;
// key → { refetch, handlers: {event: fn} } — refetched on every (re)connect
const subscriptions = new Map();
let subSeq = 0;

async function request(path, leaderId) {
  const headers = {};
  if (leaderId) headers['X-Leader-Id'] = leaderId;
  const res = await fetch(`${API_BASE}${path}`, { headers });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const err = new Error(data?.error || res.statusText);
    err.status = res.status;
    throw err;
  }
  return data;
}

function ensureSocket(leaderId) {
  if (socket && socketLeaderId === leaderId) return;
  if (socket) socket.disconnect();
  socketLeaderId = leaderId;
  socket = io(API_BASE || undefined, { auth: { leaderId } });
  socket.on('connect', () => {
    for (const sub of subscriptions.values()) sub.refetch();
  });
  socket.onAny((event, payload) => {
    for (const sub of subscriptions.values()) sub.handlers[event]?.(payload);
  });
}

function addSubscription(leaderId, { refetch, handlers = {} }) {
  ensureSocket(leaderId);
  const key = ++subSeq;
  subscriptions.set(key, { refetch, handlers });
  refetch();
  return () => {
    subscriptions.delete(key);
    if (subscriptions.size === 0 && socket) {
      socket.disconnect();
      socket = null;
      socketLeaderId = null;
    }
  };
}

// True if the ID belongs to a leader, false if not — the ID is the portal credential
export async function checkLeaderExists(leaderId) {
  try {
    await fetchLogin(leaderId);
    return true;
  } catch (err) {
    if (err.status === 404) return false;
    throw err;
  }
}

async function fetchLogin(leaderId) {
  const res = await fetch(`${API_BASE}/api/portal/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ leaderId }),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const err = new Error(data?.error || res.statusText);
    err.status = res.status;
    throw err;
  }
  return data;
}

// Live { name, balance, gift } — replaces the three separate Firebase listeners
export function subscribeLeaderInfo(leaderId, cb) {
  return addSubscription(leaderId, {
    refetch: () => request('/api/portal/me', leaderId).then(cb).catch(() => {}),
    handlers: {
      'me:updated': (me) => cb(me),
    },
  });
}

export function subscribeTransactions(leaderId, cb) {
  const refetch = () =>
    request('/api/portal/transactions', leaderId).then(cb).catch(() => {});
  return addSubscription(leaderId, {
    refetch,
    handlers: { 'tx:changed': () => refetch() },
  });
}

export function subscribeGiftHistory(leaderId, cb) {
  const refetch = () =>
    request('/api/portal/gift-history', leaderId).then(cb).catch(() => {});
  return addSubscription(leaderId, {
    refetch,
    handlers: { 'gift:changed': () => refetch() },
  });
}
